import { AlertCircle, ArrowRight, CheckCircle2, Loader2, Send, Settings } from 'lucide-react'
import { useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import MainLayout from '../components/layout/MainLayout'
import { API_URL, apiFetch } from '../config/api'

interface RoutingPriority {
  priority: string[]
  description: {
    user_cloud: string
    openclaw_local: string
    shared_pool: string
  }
}

interface InferenceResponse {
  response: string
  mode: string
  provider: string
  model: string
  tokens_used: number
  cost: number
}

export default function LLMRouter() {
  const [priority, setPriority] = useState<RoutingPriority | null>(null)
  const [loading, setLoading] = useState(true)
  const [inferenceLoading, setInferenceLoading] = useState(false)
  const [prompt, setPrompt] = useState('')
  const [forceMode, setForceMode] = useState<string>('')
  const [result, setResult] = useState<InferenceResponse | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    void fetchPriority()
  }, [])

  const fetchPriority = async () => {
    try {
      const token = localStorage.getItem('token')
      const res = await apiFetch(`${API_URL}/api/llm/routing/priority`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })

      if (!res.ok) {
        throw new Error('Failed to fetch routing priority')
      }

      const data = await res.json() as RoutingPriority
      setPriority(data)
    } catch (err) {
      console.error('Error fetching priority:', err)
      toast.error('Failed to load routing priority')
    } finally {
      setLoading(false)
    }
  }

  const handleInference = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!prompt.trim()) {
      toast.error('Please enter a prompt')
      return
    }

    setInferenceLoading(true)
    setError(null)
    setResult(null)

    try {
      const token = localStorage.getItem('token')
      const res = await apiFetch(`${API_URL}/api/llm/inference`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          prompt: prompt.trim(),
          max_tokens: 1000,
          temperature: 0.7,
          force_mode: forceMode || null,
        }),
      })

      if (!res.ok) {
        const errorData = await res.json() as { detail?: string }
        throw new Error(errorData.detail || 'Inference failed')
      }

      const data = await res.json() as InferenceResponse
      setResult(data)
      toast.success(`Inference completed via ${data.mode}`)
    } catch (err) {
      console.error('Inference error:', err)
      const errorMsg = err instanceof Error ? err.message : 'Inference failed'
      setError(errorMsg)
      toast.error(errorMsg)
    } finally {
      setInferenceLoading(false)
    }
  }

  const getModeLabel = (mode: string): string => {
    const labels: Record<string, string> = {
      user_cloud: 'User Cloud API',
      openclaw_local: 'OpenClaw Local LLM',
      shared_pool: 'AiCloud Shared Pool',
    }
    return labels[mode] || mode
  }

  const getModeColor = (mode: string): string => {
    const colors: Record<string, string> = {
      user_cloud: 'text-blue-400',
      openclaw_local: 'text-green-400',
      shared_pool: 'text-purple-400',
    }
    return colors[mode] || 'text-gray-400'
  }

  if (loading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </MainLayout>
    )
  }

  return (
    <MainLayout>
      <div className="max-w-5xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold mb-2">Unified LLM Router</h1>
          <p className="text-muted-foreground">
            Auto-routes your LLM inference based on configured priority
          </p>
        </div>

        {/* Routing Priority */}
        <div className="card mb-8">
          <div className="card-header flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Settings className="w-5 h-5" />
              <h2 className="text-xl font-semibold">Routing Priority</h2>
            </div>
          </div>
          <div className="card-body">
            {priority && priority.priority.length > 0 ? (
              <div className="space-y-4">
                <p className="text-sm text-muted-foreground mb-4">
                  Your LLM inference will try these modes in order:
                </p>
                <div className="flex items-center gap-4">
                  {priority.priority.map((mode, index) => (
                    <div key={mode} className="flex items-center gap-2">
                      <div className="flex items-center gap-2 px-4 py-2 bg-card-hover rounded-lg border border-border">
                        <span className="text-sm font-medium text-muted-foreground">
                          {index + 1}.
                        </span>
                        <span className={`font-semibold ${getModeColor(mode)}`}>
                          {getModeLabel(mode)}
                        </span>
                      </div>
                      {index < priority.priority.length - 1 && (
                        <ArrowRight className="w-4 h-4 text-muted-foreground" />
                      )}
                    </div>
                  ))}
                </div>
                <div className="mt-6 space-y-2">
                  {priority.priority.map((mode) => (
                    <div key={mode} className="flex items-start gap-2 text-sm">
                      <CheckCircle2 className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                      <div>
                        <span className="font-medium">{getModeLabel(mode)}:</span>{' '}
                        <span className="text-muted-foreground">
                          {priority.description[mode as keyof typeof priority.description]}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="text-center py-8">
                <AlertCircle className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">No LLM modes configured</p>
                <p className="text-sm text-muted-foreground mt-2">
                  Configure at least one LLM mode to use the router
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Inference Form */}
        <div className="card">
          <div className="card-header">
            <h2 className="text-xl font-semibold">Test Inference</h2>
          </div>
          <div className="card-body">
            <form onSubmit={(e) => {
              e.preventDefault()
              void handleInference(e)
            }} className="space-y-4">
              {/* Prompt */}
              <div>
                <label className="block text-sm font-medium mb-2">Prompt</label>
                <textarea
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                  placeholder="Enter your prompt..."
                  className="input w-full min-h-[120px] resize-y"
                  disabled={inferenceLoading}
                />
              </div>

              {/* Force Mode (Optional) */}
              <div>
                <label className="block text-sm font-medium mb-2">
                  Force Mode (Optional)
                </label>
                <select
                  value={forceMode}
                  onChange={(e) => setForceMode(e.target.value)}
                  className="input w-full"
                  disabled={inferenceLoading}
                >
                  <option value="">Auto (use priority)</option>
                  <option value="user_cloud">User Cloud API</option>
                  <option value="openclaw_local">OpenClaw Local LLM</option>
                  <option value="shared_pool">AiCloud Shared Pool</option>
                </select>
                <p className="text-xs text-muted-foreground mt-1">
                  Leave as &quot;Auto&quot; to use routing priority, or force a specific mode
                </p>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={inferenceLoading || !prompt.trim()}
                className="btn btn-primary w-full"
              >
                {inferenceLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4" />
                    Send Inference
                  </>
                )}
              </button>
            </form>

            {/* Error */}
            {error && (
              <div className="mt-6 p-4 bg-red-500/10 border border-red-500/20 rounded-lg">
                <div className="flex items-start gap-2">
                  <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-red-400">Error</p>
                    <p className="text-sm text-red-300 mt-1">{error}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Result */}
            {result && (
              <div className="mt-6 space-y-4">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <CheckCircle2 className="w-4 h-4 text-green-400" />
                  <span>
                    Routed to:{' '}
                    <span className={`font-semibold ${getModeColor(result.mode)}`}>
                      {getModeLabel(result.mode)}
                    </span>
                  </span>
                  <span className="mx-2">•</span>
                  <span>Provider: {result.provider}</span>
                  <span className="mx-2">•</span>
                  <span>Model: {result.model}</span>
                </div>

                <div className="p-4 bg-card-hover rounded-lg border border-border">
                  <p className="text-sm font-medium mb-2">Response:</p>
                  <p className="text-sm whitespace-pre-wrap">{result.response}</p>
                </div>

                <div className="flex items-center gap-4 text-sm text-muted-foreground">
                  <span>Tokens: {result.tokens_used}</span>
                  <span>•</span>
                  <span>
                    Cost: {result.cost === 0 ? 'Free' : `$${result.cost.toFixed(4)}`}
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </MainLayout>
  )
}
